import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from './FadeIn';
function AnimatedCounter({
  end,
  suffix = '',
  prefix = '',
  decimals = 0
}: {
  end: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end]);
  return <span data-id="element-160" data-mp="2rqw8l237a3s4">
      {prefix}
      {count.toFixed(decimals)}
      {suffix}
    </span>;
}
export function HeroSection() {
  return <section className="relative min-h-screen flex flex-col justify-center pt-24 pb-16 overflow-hidden" data-id="element-161" data-mp="3k9te7pkh2xri">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30 z-0" data-id="element-162" data-mp="2fg1t383h5s30"></div>
      <div className="absolute inset-0 bg-radial-glow z-0" data-id="element-163" data-mp="2beyf6r4zm64f"></div>

      {/* Decorative Nodes */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-164" data-mp="2p5lhet9yie59"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue/5 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '2s'
    }} data-id="element-165" data-mp="2kdtnc8rldqad"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full flex-1 flex flex-col justify-center" data-id="element-166" data-mp="2u3mv0yom5l9c">
        <div className="max-w-4xl" data-id="element-167" data-mp="382w9ntx687b7">
          <FadeIn delay={0.1} data-id="element-168" data-mp="381tm9vz24n01">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-169" data-mp="334uu3iyyeq4h">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-170" data-mp="1dqet156u85fj" />
              <span data-id="element-171" data-mp="18rv8n97w71pa">Post-Quantum Ready Infrastructure</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-172" data-mp="38u4j0xyqp68r">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-173" data-mp="38uw2xbiz5fj7">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-174" data-mp="3irr1fuf8zfxh" />
              <span data-id="element-175" data-mp="3ipxuee0q2toy">Micro-Lending</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-176" data-mp="3mu5ic8zcw5m4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-177" data-mp="3mufbu6jm0uwk">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-178" data-mp="lcmbs58szxi" />
              <span data-id="element-179" data-mp="k1i5r7vxq5v">AI Insights</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-180" data-mp="5dxdsn0of0xv">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-181" data-mp="5epa6023l2p7">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-182" data-mp="ee2deazmov1h" />
              <span data-id="element-183" data-mp="ebg4a6x3a7h5">Core Integration Gateway</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-184" data-mp="jcnfkfyfxosk">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-185" data-mp="23u6axjwkylej">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-186" data-mp="1yts0jv7iazdc" />
              <span data-id="element-187" data-mp="1utggajj2312e">AI Transformation</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-188" data-mp="1uvkupvekzigx">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1]" data-id="element-189" data-mp="1pv6p0k0gckm2">
              Quantum-Ready Infrastructure for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal to-blue" data-id="element-190" data-mp="2mn0d1d93uj1c">
                Modern Financial Institutions
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-191" data-mp="2dqhrwn747zfb">
            <p className="text-xl text-gray-400 mb-10 max-w-2xl leading-relaxed" data-id="element-192" data-mp="28u11tngcvazl">
              FIQIS delivers post-quantum security, AI-driven intelligence, and
              bank-owned revenue engines — all under your brand. Secure your
              future while unlocking new growth today.
            </p>
          </FadeIn>

          <FadeIn delay={0.4} className="flex flex-col sm:flex-row gap-4" data-id="element-193" data-mp="2l1w46folzp14">
            <a href="https://calendly.com/arthur-w-miller/30min" target="_blank" rel="noopener noreferrer" className="bg-teal hover:bg-teal-400 text-navy font-semibold px-8 py-4 rounded-md transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] flex items-center justify-center gap-2 text-lg" data-id="element-194" data-mp="2q2a57drslypn">
              Request a Meeting
              <ArrowRightIcon className="w-5 h-5" data-id="element-195" data-mp="38r6wr9vt2d46" />
            </a>
            <a href="#capabilities" className="bg-transparent border border-white/20 hover:border-white/50 text-white font-medium px-8 py-4 rounded-md transition-all duration-300 flex items-center justify-center text-lg hover:bg-white/5" data-id="element-196" data-mp="2v0v6zhmp0qg7">
              Explore Platform
            </a>
          </FadeIn>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="relative z-10 mt-20 border-y border-white/10 bg-slate/50 backdrop-blur-sm" data-id="element-197" data-mp="5pn7vpoct097">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-198" data-mp="1o1n7i5kvhog">
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x-0 md:divide-x divide-white/10" data-id="element-199" data-mp="1njw0zxrvtne">
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 first:pl-0 text-center md:text-left" data-id="element-200" data-mp="fhkoxd21mmfu">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-201" data-mp="3ivx27pqf3zd5">
                <AnimatedCounter end={99.99} decimals={2} suffix="%" data-id="element-202" data-mp="3iy9js1s9k7po" />
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-203" data-mp="3ewehov2idqau">
                Uptime SLA
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-204" data-mp="3nu8cu2ojedwf">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-205" data-mp="182ucidp2pqsa">Core</span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-206" data-mp="135u1rpha3zuu">
                Integrations
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-207" data-mp="1f7btmnutmokw">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-208" data-mp="1k5wwyw5ddodk">
                NIST PQC
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-209" data-mp="1p5rpt58pgo19">
                Compliant
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-210" data-mp="wgu5kwkxoh5v">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-211" data-mp="11g71s7xp30l4">
                Micro-Lending
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-212" data-mp="166x75l64f3ft">
                Transactions Secured
              </span>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </div>
    </section>;
}